import React, { useState, useEffect, useMemo,useCallback } from "react";


import { Button } from '@shadcn/components/ui/button'
import { Card, CardContent as CardBody, CardTitle} from '@shadcn/components/ui/card'
import { CSVLink } from "react-csv";
import useQuery from '../../helpers/JIFI/useQuery';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import moment from 'moment';
import useSKUStatus from "./helpers/useSKUStatus";
import useUpcomingStock from "./helpers/useUpcomingStock";
import debounce from 'lodash/debounce';
import { Row } from "@shadcn/components/ui/row";
import { Col } from "@shadcn/components/ui/col";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shadcn/components/ui/tooltip";
import "./all-stock.scss";

// Utility functions
const groupBy = (list, keyGetter) => {
  const map = new Map();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return mapToObj(map);
}

const mapToObj = (inputMap) => {
  let obj = {};
  inputMap.forEach(function (value, key) {
    obj[key] = value;
  });
  return obj;
}

// Preferred category display order (categories not in this list appear at the end alphabetically)
const CATEGORY_ORDER = [
  'CLASSIC',
  'LIGHT',
  'TRUNK',
  'SET',
  'BAG',
  'ACCESSORIES',
  'MISC',
]

// [column header, location name in JIFI]. Headers and cells both render from
// this, so a region total is always the sum of the columns shown beside it.
const REGION_COLUMNS = {
  AU: [
    ['EWE - Melbourne', 'EWE'],
    ['EWE - Sydney', 'EWE - Sydney'],
    ['EWE - Brisbane', 'EWE - Brisbane'],
    ['EWE - Perth', 'EWE - Perth'],
    ['EWE - Adelaide', 'EWE - Adelaide'],
    ['HQ2', 'HQ2'],
    ['QV Store', 'QV Store'],
    ['Chadstone', 'CHDRETAIL'],
    ['Doncaster', 'Doncaster'],
    ['Burnside', 'Burnside'],
    ['Canberra', 'Canberra'],
    ['Chatswood', 'Chatswood'],
    ['Galeries Store', 'Galeries Sydney'],
    ['Pitt St', 'Pitt Street'],
    ['Bondi', 'Bondi'],
    ['Rosebery', 'Rosebery'],
    ['James St', 'James St'],
    ['Karrinyup', 'Karrinyup'],
    ['Airport Sydney', 'Airport Sydney'],
  ],
  US: [
    ['GREEEN ST', 'NYC Greene St'],
    ['UNIS', 'UNIS'],
    ['KENTUCKY', 'LCHKTC'],
    ['UTAH', 'LCHSLC'],
    ['AMAZON', 'Amazon'],
  ],
  UK: [
    ['Airbox', 'Airbox UK'],
    ['Amazon UK', 'Amazon UK'],
  ],
  NZ: [
    ['EWE - New Zealand', 'EWE - New Zealand'],
  ],
}

const REGIONS = ['AU', 'US', 'UK', 'NZ']

// A cell is a bare number when everything on hand is available, and the full
// breakdown where reserved, display, quarantine or damaged stock is present.
// Available is on hand less reserved; quarantine and damaged are separate
// buckets, not part of on hand.
const cellAvailable = (cell) => (typeof cell === 'number' ? cell : (cell?.a ?? 0));

const regionTotals = (row, region) => (
  REGION_COLUMNS[region].reduce((totals, [, locationName]) => {
    const cell = row?.cells?.[locationName];
    if (cell === undefined || cell === null) return totals;
    if (typeof cell === 'number') {
      return { ...totals, available: totals.available + cell, onHand: totals.onHand + cell };
    }
    return {
      available: totals.available + (cell.a || 0),
      onHand: totals.onHand + (cell.o || 0),
      reserved: totals.reserved + (cell.r || 0),
      quarantine: totals.quarantine + (cell.q || 0),
      damaged: totals.damaged + (cell.d || 0),
    };
  }, { available: 0, onHand: 0, reserved: 0, quarantine: 0, damaged: 0 })
)

const unallocatedFor = (row, region) => row?.unallocated?.[region] || 0

const totalTooltip = (region, totals, unallocated) => [
  `${region} available = ${totals.available}`,
  '',
  `On hand      ${totals.onHand}`,
  `Reserved    -${totals.reserved}`,
  '',
  `Quarantine   ${totals.quarantine} (not in on hand)`,
  `Damaged      ${totals.damaged} (not in on hand)`,
  '',
  `Unallocated demand: ${unallocated} (not deducted — no stock is held for it)`,
].join('\n')

const cellTooltip = (locationName, cell) => {
  if (cell === undefined || cell === null) return `${locationName}: no stock`;
  if (typeof cell === 'number') {
    return [
      locationName,
      `On hand      ${cell}`,
      'Display       0',
      'Reserved    -0',
      `Available    ${cell}`,
    ].join('\n');
  }
  return [
    locationName,
    `On hand      ${cell.o || 0}`,
    `Display       ${cell.di || 0}`,
    `Reserved    -${cell.r || 0}`,
    `Available    ${cell.a || 0}`,
    ...(cell.q ? [`Quarantine   ${cell.q} (not in on hand)`] : []),
    ...(cell.d ? [`Damaged      ${cell.d} (not in on hand)`] : []),
    ...(cell.c ? [`Committed    ${cell.c} (picked or sent, not shipped; not in on hand)`] : []),
  ].join('\n');
}

// Expand All can render thousands of stock cells. Mounting a Radix Tooltip for
// every cell up front makes that interaction noticeably slower, so each
// tooltip is only mounted after its trigger is hovered for the first time.
const StockTooltip = React.memo(({ content, children }) => {
  const [isActive, setIsActive] = useState(false)

  if (!isActive) {
    return React.cloneElement(children, {
      onPointerEnter: (event) => {
        children.props.onPointerEnter?.(event)
        setIsActive(true)
      },
    })
  }

  return (
    <Tooltip defaultOpen>
      <TooltipTrigger asChild>
        {children}
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-[24rem] whitespace-pre-line rounded-md border border-gray-200 bg-white px-3 py-2 text-left text-xs leading-5 text-gray-700 shadow-lg"
      >
        {content}
      </TooltipContent>
    </Tooltip>
  )
})

const LineRenderer = React.memo(({ row, variantName, tableData, lineIndex, expandedRegions, findSKUStatus }) => {
  const normalizedSku = row.sku.split('-').filter((val) => val !== 'P' && val !== 'LE').join('-');
  const containerData = tableData?.[normalizedSku];
  const nextContainerFor = (region) => {
    const container = containerData?.[`${region.toLowerCase()}NextContainer`];
    return container?.date ? moment(container.date).format('DD-MM-YYYY') : '';
  };

  const renderCell = (content, columnIndex, key) => (
    <td
      key={key}
      className={columnIndex === 0 ? 'total-stock-first-col' : ''}
      style={{ ...(columnIndex === 0 ? {position:  'sticky', background: 'white', left: 0, zIndex: 3, textWrap: 'auto'}: {})}}
    >
      {content}
      {
        columnIndex === 0 && (
          <>
            <div style={{position: 'absolute', right: 0, top: 0, bottom: 0, width: 0, boxShadow: 'rgba(0, 0, 0, 0.25) 2px -4px 8px 2px', zIndex: 2}} />
            <div style={{position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, background: 'white', zIndex: 3}} />
          </>
        )
      }
    </td>
  );
  
  return (
    <tr>
      { renderCell(
        <div
          style={{ display: 'flex', alignItems: 'baseline', columnGap: 4, flexWrap: 'wrap' }}
          title={variantName ? `${row.sku} (${variantName})` : row.sku}
        >
          <span>{row.sku}</span>
          {variantName && (
            <span style={{ color: '#6c757d', minWidth: 0, maxWidth: '100%', whiteSpace: 'normal' }}>
              {` (${variantName})`}
            </span>
          )}
        </div>, 0, `sku-${lineIndex}-1`
      )}

      {REGIONS.map((region) => {
        const totals = regionTotals(row, region);
        const unallocated = unallocatedFor(row, region);

        return (
          <React.Fragment key={`${region}-${lineIndex}`}>
            {renderCell(
              <StockTooltip content={totalTooltip(region, totals, unallocated)}>
                <b className="cursor-help">
                  {totals.available}
                </b>
              </StockTooltip>,
              1,
              `${region}-available-${lineIndex}`
            )}

            {expandedRegions[region] && (
              <>
                {renderCell(findSKUStatus(row.sku, region), 2, `${region}-status-${lineIndex}`)}
                {renderCell(nextContainerFor(region), 3, `${region}-container-${lineIndex}`)}
                {renderCell(
                  <span className="text-danger">{unallocated}</span>,
                  4,
                  `${region}-unallocated-${lineIndex}`
                )}
                {REGION_COLUMNS[region].map(([header, locationName]) => {
                  const cell = row?.cells?.[locationName];
                  return renderCell(
                    <StockTooltip content={cellTooltip(header, cell)}>
                      <span className="cursor-help">
                        {cellAvailable(cell)}
                      </span>
                    </StockTooltip>,
                    5,
                    `${region}-${locationName}-${lineIndex}`
                  );
                })}
              </>
            )}
          </React.Fragment>
        );
      })}
    </tr>
  );
});

const CategoryRow = React.memo(({ category, categoryGroup, index, expandedProducts, searchTerm, toggleProductSelected, expandedRegions, tableData, findSKUStatus }) => {
  const handleCatClicked = () => {
    let allProducts = categoryGroup.map(groupedProduct => groupedProduct.product._id);
    let checker = (arr, target) => target.every(v => arr.includes(v));

    if (checker(expandedProducts, allProducts)) {
      toggleProductSelected([]);
    } else {
      toggleProductSelected(allProducts);
    }
  };

  return (
    <React.Fragment key={`cat-${category}${index}`}>
      <tr className="bg-black"  onClick={handleCatClicked} style={{ userSelect: 'none', color: '#fff', cursor: 'pointer' }}>
        <td className="total-stock-first-col" style={{position:  'sticky', left: 0, zIndex: 8, background: '#000' }}><b>{category}</b></td>
      </tr>
      {categoryGroup.map((groupedProduct, productIndex) => {
        const { product, lines } = groupedProduct;
        const isExpanded = expandedProducts.includes(product._id) || searchTerm !== '';

        if (lines.length === 0) return null;

       

        return (
          <React.Fragment key={`prod-${product.name}${productIndex}`}>
            <tr style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleProductSelected([product._id])}>
              <td className="total-stock-first-col" style={{position:  'sticky', background: 'white', left: 0}}><b style={{maxWidth: '100%', textWrap: 'auto'}}>{product.name}</b></td>
            </tr>
        

            {isExpanded && lines.map(({ row, variantName }, lineIndex) => {
              if (row.sku !== '') {
                return (
                  <LineRenderer
                    findSKUStatus={findSKUStatus}
                    key={`line-${lineIndex}`}
                    row={row}
                    variantName={variantName}
                    lineIndex={lineIndex}
                    expandedRegions={expandedRegions}
                    tableData={tableData}
                  />
                )
              }
            })}
          </React.Fragment>
        );
      })}
    </React.Fragment>
  );
});

const BasicTable = ({fromProduct, productID, skus}) => {
  const [refresh, setRefresh] = useState(true);
  const [expandedRegions, setExpandedRegions] = useState(
    Object.fromEntries(REGIONS.map((region) => [region, false]))
  );
  const toggleRegionExpanded = useCallback((region) => {
    setExpandedRegions((prev) => ({ ...prev, [region]: !prev[region] }));
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');

  const refreshData = () => {
    setRefresh(prev => !prev);
  }


  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 300),
    []
  );

  const handleChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      setSearchTerm(inputValue);
      debouncedSearch.cancel(); 
    }
  };

  const findSKUStatus = useSKUStatus();


  const [data, state] = useQuery({
    url: `/product/total-stock-availability`,
    queryData: {
      skus: skus,
    },
    refresh,
  });

  const stockRows = useMemo(() => (Array.isArray(data?.rows) ? data.rows : []), [data]);

  const [products, productState] = useQuery({
    url: `/product/findAllTotalStock`,
    refresh,
    queryData: {
      productID: productID,
    },
  });

  const [containers, containerState] = useQuery({
    url: `/inventory/fetchUpcomingContainerTotalStock/`,
    refresh,
  });

  const [tableData, setTableData] = useState({});

  useEffect(() => {
    let toUseContainers = containers || []
    
    if (products) {
    
      const updatedTable = {};

      products?.data.forEach(product => {
        product.variants.forEach(variant => {
          const sku = variant.sku;

          let usLocations = ['USA - Dallas', 'UNIS', 'LCHSLC', 'LCHKTC'];
          let ukLocations = ['Airbox UK', 'Amazon UK'];

          let containerLineData = [...toUseContainers].reverse().map(container => {
            let containerLine = container.containerLines.find(containerLine => containerLine?.sku === sku);
            let count = containerLine == undefined ? 0 : containerLine.quantity;

            let location = container.landingLocation.name;
            let date = container.arrivalDate;
            return {
              count,
              location,
              date,
              country: usLocations.includes(location) ? 'US' : ukLocations.includes(location) ? 'UK' : 'AU'
            };
          }).filter(val => val.count !== 0);

          let usList = containerLineData.filter(val => val.country === 'US');
          let auList = containerLineData.filter(val => val.country === 'AU');
          let ukList = containerLineData.filter(val => val.country === 'UK');

          updatedTable[sku] = {
            usNextContainer: usList.length > 0 ? usList[0] : null,
            auNextContainer: auList.length > 0 ? auList[0] : null,
            ukNextContainer: ukList.length > 0 ? ukList[0] : null,
          };
        });
      });

      setTableData(updatedTable);
    }
  }, [products, containers]);


  const [groupedData, setGroupedData] = useState([]);
  const [categories, setCategories] = useState(CATEGORY_ORDER);
  const [expandedProducts, setExpandedProducts] = useState([]);

  const toggleProductSelected = (ids) => {
    setExpandedProducts(prev => {
      const newExpandedProducts = new Set(prev);
      ids.forEach(id => {
        if (newExpandedProducts.has(id)) {
          newExpandedProducts.delete(id);
        } else {
          newExpandedProducts.add(id);
        }
      });
      return Array.from(newExpandedProducts);
    });
  }

  useEffect(() => {
    if (!data || !products) {
      return;
    }

    let internalGroupedData = [];
    const stockRowBySku = new Map(stockRows.map(row => [row.sku, row]));

    products?.data.forEach(product => {
      let lines = [];
      product.variants.forEach(variant => {
        let foundRow = stockRowBySku.get(variant.sku);
        const normalizedSearchTerm = searchTerm.toUpperCase();
        const matchesSearch = searchTerm === '' ||
          foundRow?.sku?.toUpperCase().includes(normalizedSearchTerm) ||
          variant.name?.toUpperCase().includes(normalizedSearchTerm);
        if (variant.isArchived !== true && foundRow && matchesSearch) {
          lines.push({
            row: foundRow,
            variantName: variant.name,
          });
        }
      });
      if (product.isArchived !== true && lines.length !== 0) {
        internalGroupedData.push({
          product: {
            ...product,
            categoryIndex: (() => { const idx = CATEGORY_ORDER.indexOf(product.category); return idx === -1 ? CATEGORY_ORDER.length : idx; })(),
          },
          lines: lines.sort((a, b) => a.row.sku.localeCompare(b.row.sku))
        });
      }
    });

    internalGroupedData = internalGroupedData.filter(val => val.lines.length !== 0).sort((a, b) => a.product.name.localeCompare(b.product.name));
    const categoryGrouped = groupBy(internalGroupedData, productGroup => productGroup.product.category);

    // Derive categories: preferred order first, then any new ones alphabetically
    const allCategories = Object.keys(categoryGrouped);
    const ordered = CATEGORY_ORDER.filter(c => allCategories.includes(c));
    const extra = allCategories.filter(c => !CATEGORY_ORDER.includes(c)).sort();
    const nextCategories = [...ordered, ...extra];

    setCategories(nextCategories);

    setGroupedData(categoryGrouped);

  }, [stockRows, data, products, setGroupedData, searchTerm]);

  const dateString = (new Date()).toDateString().replace(' ', '-').replace(' ', '-').replace(' ', '-');

  const csvData = useMemo(() => {
    // Headers and cells both come from REGION_COLUMNS, so the CSV cannot drift
    // away from what the table shows.
    const headers = [
      'SKU',
      'Variant Name',
      ...REGIONS.flatMap((region) => [
        `${region} AVAILABLE`,
        `${region} STATUS`,
        `${region} CONTAINER`,
        `UNALLOCATED ${region}`,
        ...REGION_COLUMNS[region].map(([header]) => header),
      ]),
    ];

    if (!data || !products) {
      return [headers];
    }

    const stockRowBySku = new Map(stockRows.map(row => [row.sku, row]));
    const categoryGrouped = groupBy(
      products.data
        .filter(product => product.isArchived !== true)
        .map(product => ({
          product,
          lines: product.variants
            .filter(variant => variant.isArchived !== true && stockRowBySku.has(variant.sku))
            .map(variant => ({ row: stockRowBySku.get(variant.sku), variantName: variant.name }))
            .sort((a, b) => a.row.sku.localeCompare(b.row.sku)),
        }))
        .filter(group => group.lines.length !== 0)
        .sort((a, b) => a.product.name.localeCompare(b.product.name)),
      productGroup => productGroup.product.category
    );

    const orderedRows = [];
    categories.forEach(category => {
      (categoryGrouped[category] || []).forEach(({ lines }) => {
        lines.forEach(({ row, variantName }) => {
          if (row.sku === '') return;

          const normalizedSku = row.sku.split('-').filter((val) => val !== 'P' && val !== 'LE').join('-');
          orderedRows.push([
            row.sku,
            variantName || '',
            ...REGIONS.flatMap((region) => {
              const container = tableData[normalizedSku]?.[`${region.toLowerCase()}NextContainer`];
              return [
                regionTotals(row, region).available,
                findSKUStatus(row.sku, region),
                container?.date ? moment(container.date).format('DD-MM-YYYY') : '',
                unallocatedFor(row, region),
                ...REGION_COLUMNS[region].map(([, locationName]) => cellAvailable(row?.cells?.[locationName])),
              ];
            }),
          ]);
        });
      });
    });

    return [headers, ...orderedRows];
  }, [stockRows, data, products, tableData, findSKUStatus, categories]);
  const allSelected = expandedProducts?.length === products?.data?.length || searchTerm !== '';
  const toggleProductSelectedAll = () => {
    if (allSelected) {
      setExpandedProducts([]);
      setSearchTerm('');
    } else {
      setExpandedProducts(products?.data.map(product => product._id));
    }
  }

  if(fromProduct && !productID) {
    return <div>No Data</div>
  }

  return (
    <TooltipProvider delayDuration={100} skipDelayDuration={50}>
      <div className={`${!productID ? 'page-content' :""}`}>
        <div className={`${!productID ? 'container-fluid' :""}`}>
        {!productID && <Breadcrumbs title="Inventory" breadcrumbItem="All Locations" />}
        <div>
          {!productID && <Row className="total-stock-toolbar">
            <div className="total-stock-toolbar-action">
              <Button type="button" variant={!allSelected ? 'success' : 'warning'} className="btn-lg btn-rounded" onClick={toggleProductSelectedAll}> {!allSelected ? 'Expand All' : searchTerm !== '' ? 'Close All & Clear' : 'Close All'} </Button>
            </div>
            <div className="total-stock-toolbar-search">
              <div className="app-search">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search, then press Enter..."
                    value={inputValue}
                    onChange={handleChange}
                    onKeyPress={handleKeyPress}      
                  />
                  <span className="bx bx-search-alt"></span>
                </div>
              </div>
            </div>
            <div className="total-stock-toolbar-export">
              <CSVLink
                style={{ float: 'right' }}
                data={csvData}
                filename={`TOTAL_STOCK_${dateString}.csv`}
              >
                <Button type="button" variant="secondary" className="btn-lg btn-rounded">
                  CSV
                </Button>
              </CSVLink>
            </div>
          </Row>}
        </div>
        <Row>
          <Col>
            <Card className='pt-6 mb-6 rounded-none border-0'>
              <CardBody>
               {  !productID && <CardTitle className="text-[15px] mb-2 pl-2">Total Stock Levels</CardTitle>}
                <div style={{ display: 'block', width: '100%', overflowX: 'auto', overflowY: 'scroll', height: 'calc(70vh)', whiteSpace: 'nowrap' }}>
                  {data ? (
                    <table className="table mb-0 total-stock-table">
                      <colgroup>
                        <col className="total-stock-first-col" />
                      </colgroup>
                      <thead style={{ position: 'sticky', top: -1, zIndex: 9, background: 'white' }}>
                        <tr>
                          <th className="total-stock-first-col cursor-default" style={{position: 'sticky', left: 0, zIndex: 11, background: 'white'}}></th>
                          {REGIONS.map((region) => (
                            <React.Fragment key={`head-${region}`}>
                              <th
                                className="cursor-pointer"
                                onClick={() => toggleRegionExpanded(region)}
                              >
                                <span className="inline-flex items-center">
                                  <b>{region} Available</b>
                                  {expandedRegions[region] ? (
                                    <i className="bx bx-caret-right"></i>
                                  ) : (
                                    <i className="bx bx-caret-down"></i>
                                  )}
                                </span>
                              </th>
                              {expandedRegions[region] && (
                                <>
                                  <th>STATUS</th>
                                  <th>CONTAINER</th>
                                  <th>UNALLOCATED {region}</th>
                                  {REGION_COLUMNS[region].map(([header]) => (
                                    <th key={`head-${region}-${header}`}>{header}</th>
                                  ))}
                                </>
                              )}
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map((category, index) => {
                          const categoryGroup = groupedData[category];
                          if (!categoryGroup) return null;

                          return (
                            <CategoryRow
                              findSKUStatus={findSKUStatus}
                              key={`cat-${category}${index}`}
                              category={category}
                              categoryGroup={categoryGroup}
                              index={index}
                              expandedProducts={expandedProducts}
                              searchTerm={searchTerm}
                              toggleProductSelected={toggleProductSelected}
                              expandedRegions={expandedRegions}
                              tableData={tableData}
                            />
                          );
                        })}
                      </tbody>
                    </table>
                  ) : state === 'error' ? (
                    <div className="flex items-center justify-center p-4 text-danger">
                      Failed to load total stock.
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <div className="spinner-border text-primary m-1" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        </div>
      </div>
    </TooltipProvider>
  );
}

export default BasicTable;
